import React, { useState, useCallback } from 'react';
import { buildMOAPayload } from '../utils/strategyPayloadBuilders';
import { useInitialConfig } from '../utils/useInitialConfig';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogActions, DialogContent,
  Button, Tabs, Tab, Box, Typography, CircularProgress
} from '@mui/material';
import HelpIcon from './HelpIcon';
import { useAuth } from '../context/AuthContext';
import { useChartOverlays } from '../context/ChartOverlayContext';
import { addAreaHighlightOverlaysAndMessages } from './addAreaHighlightOverlaysUtil';
import { formatMessagesPanelTabs } from '../utils/messagesPanelUtils';
import MOAGroup1Tab from './MOAGroup1Tab';
import MOAGroup2Tab, { GROUP2_INIT } from './MOAGroup2Tab';
import MOAZodiacHelioTab, { ZODIAC_HELIO_INIT } from './MOAZodiacHelioTab';
import MOAGroup3Tab from './MOAGroup3Tab';
import MOAGroup4Tab from './MOAGroup4Tab';
import MOAGroup5Tab from './MOAGroup5Tab';
import MOAGroup6Tab from './MOAGroup6Tab';
import MOAGroup7Tab from './MOAGroup7Tab';
import MOAGroup7BTab from './MOAGroup7BTab';
import MOAExecuteTab from './MOAExecuteTab';

/**
 * The tabs, named for what each one studies rather than by position.
 *
 * "Group 4" told a reader only that it came after Group 3. The "++" pairs are the extended form of
 * the tab they follow — Transit++ builds on Transit, Origin++ on Origin — which is the one thing
 * the numbering did convey and the names keep.
 *
 * Each entry carries its own renderer, so a label and the panel behind it move together. They used
 * to be a list of strings beside an `if (idx === 0) … if (idx === 1) …` chain, which meant
 * inserting a tab anywhere but the end silently repointed every panel after it at the wrong
 * label — and inserting Zodiac-Helio at position 2 is exactly that edit.
 */
const TABS = [
  { label: 'Transit',      render: (c) => <MOAGroup1Tab       onAddCondition={c.add} forms={c.g1Forms} onFormsChange={c.setG1Forms} /> },
  { label: 'Zodiac-Geo',   render: (c) => <MOAGroup2Tab       onAddCondition={c.add} forms={c.g2Forms} onFormsChange={c.setG2Forms} /> },
  { label: 'Zodiac-Helio', render: (c) => <MOAZodiacHelioTab  onAddCondition={c.add} forms={c.zhForms} onFormsChange={c.setZhForms} /> },
  { label: 'Esoterica',    render: (c) => <MOAGroup3Tab       onAddCondition={c.add} forms={c.g3Forms} onFormsChange={c.setG3Forms} /> },
  { label: 'Transit++',    render: (c) => <MOAGroup4Tab       onAddCondition={c.add} forms={c.g4Forms} onFormsChange={c.setG4Forms} /> },
  { label: 'Esoterica++',  render: (c) => <MOAGroup5Tab       onAddCondition={c.add} forms={c.g5Forms} onFormsChange={c.setG5Forms} /> },
  { label: 'Vedic',        render: (c) => <MOAGroup6Tab       onAddCondition={c.add} forms={c.g6Forms} onFormsChange={c.setG6Forms} /> },
  { label: 'Origin',       render: (c) => <MOAGroup7Tab       natal={c.g7Natal} onNatalChange={c.setG7Natal} chartData={c.g7ChartData} onChartDataChange={c.setG7ChartData} /> },
  { label: 'Origin++',     render: (c) => <MOAGroup7BTab      onAddCondition={c.add} forms={c.g7Forms} onFormsChange={c.setG7Forms} natal={c.g7Natal} /> },
  { label: 'Run Study',    render: (c) => c.renderExecuteTab() },
];

const TAB_LABELS = TABS.map(t => t.label);

/** The last tab is the one that runs the study, and the only one showing the run button. */
const RUN_STUDY_TAB = TABS.length - 1;

// Default form states for each group tab (exported from tab files for reference)
const G1_INIT = {
  r1:  { planet: 'Sun',     longitude: '',   sign: 'Aries',    nDays: '7', windowMode: 'before', value: '2' },
  r2:  { planet1: 'Sun',   planet2: 'Moon', diff: '',          nDays: '7', windowMode: 'before', value: '2' },
  r3:  { planet1: 'Sun',   planet2: 'Moon', diff: '',          nDays: '7', windowMode: 'before', value: '2' },
  r4:  { planet: 'Sun',    longitude: '',                      nDays: '7', windowMode: 'before', value: '2' },
  r5:  { planet: 'Sun',    declination: '',                    nDays: '7', windowMode: 'before', value: '2' },
  r6:  { planet: 'Sun',    sign: 'Aries',                      value: '2' },
  r7:  { planet: 'Sun',    sign: 'Aries',                      value: '2' },
  r8:  { planet: 'Moon',   nakshatra: 'Ashwini',               value: '2' },
  r9:  { planet: 'Moon',   hemisphere: 'East',                 value: '2' },
  r10: { planet: 'Moon',   hemisphere: 'East',                 value: '2' },
  r11: { planet: 'Mercury',hemisphere: 'East',                 value: '2' },
  r12: { planet: 'Mercury',hemisphere: 'East',                 value: '2' },
};
// r5, r6 and r8 are absent on purpose: those were the heliocentric rows, and they now live in
// ZODIAC_HELIO_INIT alongside the tab that owns them.
// Zodiac-Geo's rows now come from the tab itself, as the heliocentric ones already did. The copy
// that used to sit here had to be edited in step with the tab, and when it was not the new row
// rendered against undefined state and threw.
const G3_INIT = {
  r7: { planet1: 'Mercury', planet2: 'Sun', position: 'Ahead of', value: '2' },
  r8:  { value: '2' },
  r9:  { value: '2' },
  r10: { status: 'Weak',        value: '2' },
  r11: { status: 'Auspicious',  value: '2' },
  r12: { planet: 'Mars', value: '2' },
  r13: { planet: 'Mars', value: '2' },
  r14: { planet: 'Venus', conjType: 'Superior', nDays: '7', windowMode: 'before', value: '2' },
};
const G4_INIT = {
  r1:  { planet: 'Sun', harmonic: '2', offsetDeg: '2', withinHarmonic: '2',   value: '2' },
  r2:  { planet1: 'Sun', planet2: 'Moon', fromDecDiff: '2', toDeg: '2',       value: '2' },
  r3:  { planet: 'Sun', fromDecl: '2', toDecl: '2',                           value: '2' },
  r4:  { planet: 'Sun', distCat: 'Close',                                     value: '2' },
  r5:  { planet: 'Sun', speedCat: 'Faster than average',                      value: '2' },
  r6:  { planet1: 'Sun', planet2: 'Moon', nakshatra: 'Ashwini', charan: '1', value: '2' },
  r7:  { planet1: 'Sun', planet2: 'Moon', sign: 'Aries',                      value: '2' },
  r8:  { planet1: 'Sun', planet2: 'Moon', nakshatra: 'Ashwini',               value: '2' },
  r9:  { planet: 'Sun', fromDeg: '2', toDeg: '2',                             value: '2' },
  r10: { planet1: 'Sun', planet2: 'Moon', fromDeg: '2', toDeg: '2',          value: '2' },
  r12: { planet1: 'Sun', planet2: 'Moon', planet3: 'Mercury', tolerance: '5', value: '2' },
  r13: { planet1: 'Sun', planet2: 'Moon', sign: 'Aries',                      value: '2' },
  r14: { planet1: 'Sun', planet2: 'Moon', nakshatra: 'Ashwini',               value: '2' },
};
const G5_INIT = {
  r1: { planet: 'Sun',    state: 'Average',      value: '2' },
  r2: { day: '1',         nDays: '7', windowMode: 'before', value: '2' },
  r3: { month: 'January',                         value: '2' },
  r4: { paksha: 'Shukla', tithi: 'Pratipada', nDays: '7', windowMode: 'before', value: '2' },
  r5: { weekday: 'Monday', nDays: '7', windowMode: 'before', value: '2' },
  r9: { yog: 'Vishkumbha', nDays: '7', windowMode: 'before', value: '2' },
  r6: { vedicMonth: 'Chaitra',                    value: '2' },
  r7: { dayLord: 'Moon', planet: 'Mercury', aspectDeg: '2', nDays: '7', windowMode: 'before', value: '2' },
  r8: { dayLord: 'Moon', sign: 'Aries', nDays: '7', windowMode: 'before', value: '2' },
};
const G6_INIT = {
  r1:  { planet1: 'Sun', planet2: 'Moon', numSigns: '1',          value: '2' },
  r2:  { planet1: 'Sun', planet2: 'Moon', numSigns: '1',          value: '2' },
  r3:  { planet1: 'Sun', planet2: 'Moon', numNakshatra: '1',      value: '2' },
  r4:  { planet1: 'Sun', planet2: 'Moon', aspectType: 'all',      value: '2' },
  r5:  { planet: 'Sun',  sign: 'Aries',   aspectType: 'all',      value: '2' },
  r6:  { planet: 'Sun',  sign: 'Aries',   value: '2' },
  r7:  { planet: 'Sun',  nakshatra: 'Ashwini', value: '2' },
  r8:  { planet1: 'Sun', planet2: 'Moon', planet3: 'Mars', tolerance: '2', value: '2' },
  r9:  { planet: 'Sun',                                           value: '2' },
  r10: { planet: 'Sun',                                           value: '2' },
  r11: { planet1: 'Sun', planet2: 'Moon', aspectDeg: '0', value: '2' },
  r12: { planet1: 'Sun', planet2: 'Moon', aspectDeg: '0', sign: 'Aries', value: '2' },
};
const G7_INIT = {
  r1: { planet1: 'Sun', longitude: '', value: '2' },
  r2: { planet1: 'Sun', planet2: 'Moon', value: '2' },
  r3: { planet1: 'Sun', planet2: 'Moon', value: '2' },
  r4: { planet1: 'Sun', mode: 'rising', value: '2' },
  r5: { planet1: 'Sun', mode: 'rising', value: '2' },
};
const G7_NATAL_INIT = { date: '', time: '', longitude: '-74.01', latitude: '40.71', utcOffset: -5, tz: 'EST' };

/**
 * The top-level prefill keys this dialog's applier reads.
 *
 * <p>⚠ EXPORTED SO AN UNKNOWN FIELD CAN BE REFUSED RATHER THAN SILENTLY DROPPED. A prefill
 * naming a field that does not exist would otherwise open the dialog, change nothing and report
 * success — which for a Copilot is the "a control that lies" failure, in the one place the
 * caller has no way to notice it. Same precedent as `ctsDirection`, which refuses an unknown
 * name with the valid set listed rather than quietly running the default.
 *
 * <p>Nested keys are deliberately NOT listed: `mergeInto` already ignores sub-keys it does not
 * know, and enumerating them here would be a second declaration of every form's shape.
 *
 * <p>`prefillFields.test.js` reads the applier and fails if the two drift apart.
 */
export const PREFILL_FIELDS = Object.freeze([
  'conditions', 'timeWindowMode', 'twNBefore', 'twNAfter', 'twNAround1', 'twNAround2',
  'selMode', 'exactVal', 'gteVal', 'lteVal', 'rangeGte', 'rangeLte', 'color',
]);

const MOAStrategyDialog = ({ open, onClose, barData, extendedBarData,
                            setMessagesTabContents, setMessagesOpen, setStudyTitle, onJournalAppend,
                            initialConfig = null }) => {
  const { api, token, isAdmin } = useAuth();
  const { addOverlay } = useChartOverlays();
  const [activeTab, setActiveTab] = useState(0);
  const [conditions, setConditions] = useState([]);

  // Lifted form state for each Group tab — persists across tab switches
  const [g1Forms, setG1Forms] = useState(G1_INIT);
  const [g2Forms, setG2Forms] = useState(GROUP2_INIT);
  const [zhForms, setZhForms] = useState(ZODIAC_HELIO_INIT);
  const [g3Forms, setG3Forms] = useState(G3_INIT);
  const [g4Forms, setG4Forms] = useState(G4_INIT);
  const [g5Forms, setG5Forms] = useState(G5_INIT);
  const [g6Forms, setG6Forms] = useState(G6_INIT);
  const [g7Forms, setG7Forms] = useState(G7_INIT);
  const [g7Natal, setG7Natal] = useState(G7_NATAL_INIT);
  const [g7ChartData, setG7ChartData] = useState(null);

  const [timeWindowMode, setTimeWindowMode] = useState('before');
  const [twNBefore, setTwNBefore]   = useState('7');
  const [twNAfter, setTwNAfter]     = useState('7');
  const [twNAround1, setTwNAround1] = useState('7');
  const [twNAround2, setTwNAround2] = useState('7');

  // Selection criteria state (lifted from MOAExecuteTab so EXECUTE can access)
  const [selMode, setSelMode]       = useState('gte');
  const [exactVal, setExactVal]     = useState('0');
  const [gteVal, setGteVal]         = useState('0');
  const [lteVal, setLteVal]         = useState('0');
  const [rangeGte, setRangeGte]     = useState('0');
  const [rangeLte, setRangeLte]     = useState('0');
  const [color, setColor]           = useState('#E67C73');

  const [executing, setExecuting]   = useState(false);
  const [result, setResult]         = useState(null);

  /**
   * PREFILL. `conditions` REPLACES -- the condition list IS the study. Every scalar is applied
   * only when named, so a prefill that sets the colour leaves the time window alone.
   */
  useInitialConfig(initialConfig, (cfg) => {
    if (Array.isArray(cfg.conditions)) setConditions(cfg.conditions);
    if (cfg.timeWindowMode !== undefined) setTimeWindowMode(cfg.timeWindowMode);
    if (cfg.twNBefore !== undefined) setTwNBefore(String(cfg.twNBefore));
    if (cfg.twNAfter !== undefined) setTwNAfter(String(cfg.twNAfter));
    if (cfg.twNAround1 !== undefined) setTwNAround1(String(cfg.twNAround1));
    if (cfg.twNAround2 !== undefined) setTwNAround2(String(cfg.twNAround2));
    if (cfg.selMode !== undefined) setSelMode(cfg.selMode);
    if (cfg.exactVal !== undefined) setExactVal(String(cfg.exactVal));
    if (cfg.gteVal !== undefined) setGteVal(String(cfg.gteVal));
    if (cfg.lteVal !== undefined) setLteVal(String(cfg.lteVal));
    if (cfg.rangeGte !== undefined) setRangeGte(String(cfg.rangeGte));
    if (cfg.rangeLte !== undefined) setRangeLte(String(cfg.rangeLte));
    if (cfg.color !== undefined) setColor(cfg.color);
  });

  const isNonZero = (v) => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0;
  };
  const isExecuteReady =
    timeWindowMode === 'before' ? isNonZero(twNBefore)
    : timeWindowMode === 'after' ? isNonZero(twNAfter)
    : timeWindowMode === 'around' ? (isNonZero(twNAround1) && isNonZero(twNAround2))
    : false;

  const handleExecute = useCallback(async () => {
    /*
     * Built by the SHARED builder since 2026-10-24, so EXECUTE here and a headless
     * `strategy.run` send the same request. The builder also owns the refusal: it returns null
     * when no bars are loaded, which is the one case this used to alert on.
     */
    const payload = buildMOAPayload({
      conditions,
      timeWindowMode, twNBefore, twNAfter, twNAround1, twNAround2,
      selMode, exactVal, gteVal, lteVal, rangeGte, rangeLte,
      color,
    }, { barData, extendedBarData });
    if (!payload) { alert('No chart data loaded.'); return; }
    const dates = payload.priceDates;
    const ohlc = payload.ohlc;

    setExecuting(true);
    setResult(null);
    try {
      console.log('[MOA] Executing strategy:', { ...payload, priceDates: `[${dates.length} dates]`, ohlc: `[${ohlc.length} bars]` });
      const response = await api.post('/api/moa-strategy', payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = response.data;
      console.log('[MOA] Result:', data);
      setResult(data);

      // Paint chart: area highlights, vertical lines, trendlines
      const datePairs    = Array.isArray(data.datePairs) ? data.datePairs : [];
      const summaryLines = Array.isArray(data.summaryLines) ? data.summaryLines : [];
      let futureDates    = Array.isArray(data.futureDates) ? data.futureDates : [];

      // Fallback: extract future date pairs from datePairs if futureDates is empty
      if (futureDates.length === 0 && datePairs.length > 0) {
        const today = new Date().toISOString().slice(0, 10);
        const futurePairs = datePairs.filter(p => p.endDate && p.endDate >= today);
        futureDates = futurePairs.map(p => `${p.startDate}-to-${p.endDate}`);
      }

      addAreaHighlightOverlaysAndMessages({
        isAdmin,
        datePairs,
        summaryLines,
        futureDates,
        params: { color: data.color || color, label: 'MOA Strategy' },
        addOverlay,
        barData,
        extendedBarData,
        updateMessagesPanel: ({ summaryLines: sl, futureDates: fd }) => {
          const tabContents = formatMessagesPanelTabs(sl, fd);
          if (typeof setStudyTitle === 'function') setStudyTitle('MOA Strategy');
          if (typeof setMessagesTabContents === 'function') setMessagesTabContents(tabContents);
          if (typeof setMessagesOpen === 'function') setMessagesOpen(true);
        },
        onJournalAppend,
      });
    } catch (err) {
      console.error('[MOA] Execute error:', err);
      alert(`MOA Strategy failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setExecuting(false);
    }
  }, [api, token, barData, extendedBarData, conditions, timeWindowMode, twNBefore, twNAfter, twNAround1, twNAround2, selMode, exactVal, gteVal, lteVal, rangeGte, rangeLte, color, isAdmin, addOverlay, setStudyTitle, setMessagesTabContents, setMessagesOpen, onJournalAppend]);

  const handleAddCondition = (condObj) => {
    setConditions(prev => [...prev, condObj]);
  };

  const handleDeleteCondition = (idx) => {
    setConditions(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDeleteAllConditions = () => {
    setConditions([]);
  };

  const renderExecuteTab = () => (
    <MOAExecuteTab
      conditions={conditions}
      onDeleteCondition={handleDeleteCondition}
      onDeleteAllConditions={handleDeleteAllConditions}
      timeWindowMode={timeWindowMode}
      onTimeWindowModeChange={setTimeWindowMode}
      twNBefore={twNBefore}   onTwNBeforeChange={setTwNBefore}
      twNAfter={twNAfter}     onTwNAfterChange={setTwNAfter}
      twNAround1={twNAround1} onTwNAround1Change={setTwNAround1}
      twNAround2={twNAround2} onTwNAround2Change={setTwNAround2}
      selMode={selMode} onSelModeChange={setSelMode}
      exactVal={exactVal} onExactValChange={setExactVal}
      gteVal={gteVal} onGteValChange={setGteVal}
      lteVal={lteVal} onLteValChange={setLteVal}
      rangeGte={rangeGte} onRangeGteChange={setRangeGte}
      rangeLte={rangeLte} onRangeLteChange={setRangeLte}
      color={color} onColorChange={setColor}
      result={result}
    />
  );

  // Everything a tab's renderer might need, passed as one object so TABS stays a flat table.
  const tabContext = {
    add: handleAddCondition,
    g1Forms, setG1Forms, g2Forms, setG2Forms, zhForms, setZhForms,
    g3Forms, setG3Forms, g4Forms, setG4Forms, g5Forms, setG5Forms,
    g6Forms, setG6Forms, g7Forms, setG7Forms,
    g7Natal, setG7Natal, g7ChartData, setG7ChartData,
    renderExecuteTab,
  };

  const renderTabContent = (idx) => {
    const tab = TABS[idx];
    if (!tab) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
          <Typography sx={{ color: '#aaa', fontSize: 18 }}>Unknown tab</Typography>
        </Box>
      );
    }
    return tab.render(tabContext);
  };

  // The Paper is sized wider than any named breakpoint on purpose. This was already maxWidth="xl"
  // — 1536px, the largest MUI offers — and the longest Esoterica++ row still could not fit its ADD
  // button on one line, so stepping down to "lg" would have made it worse rather than better.
  // maxWidth={false} lifts the cap and the width below takes the viewport instead, bounded so it
  // never overflows a laptop screen nor stretches past a readable width on a very wide monitor.
  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth
      PaperProps={{ sx: { maxHeight: '90vh', height: '90vh', width: 'min(1800px, 96vw)' } }}>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ flexGrow: 1, textAlign: 'center', paddingLeft: '48px' }}>MOA Strategy Designer</span>
        <HelpIcon helpKey="moaStrategyDialog" />
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs value={activeTab} onChange={(_, newVal) => setActiveTab(newVal)} variant="scrollable" scrollButtons="auto">
          {TAB_LABELS.map((label, idx) => (
            // Run Study is bold: it is the one tab that does something rather than collecting
            // something, and it is the end of the workflow the other nine feed.
            <Tab key={idx} label={label}
              sx={idx === RUN_STUDY_TAB ? { fontWeight: 'bold' } : undefined} />
          ))}
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, overflow: 'auto', flex: 1 }}>
        {TAB_LABELS.map((_, idx) => (
          <Box key={idx} sx={{ display: activeTab === idx ? 'block' : 'none' }}>
            {renderTabContent(idx)}
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ p: '16px 24px', justifyContent: 'space-between' }}>
        <Button onClick={onClose} variant="contained" color="inherit">CLOSE</Button>
        {activeTab === RUN_STUDY_TAB && (
          <Button variant="contained"
            sx={{ backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' }, minWidth: 120, fontSize: 15 }}
            disabled={!isExecuteReady || executing}
            onClick={handleExecute}>
            {executing ? <><CircularProgress size={18} sx={{ color: '#fff', mr: 1 }} /> Running...</> : 'EXECUTE'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

MOAStrategyDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  barData: PropTypes.object,
  extendedBarData: PropTypes.object,
  setMessagesTabContents: PropTypes.func,
  setMessagesOpen: PropTypes.func,
  setStudyTitle: PropTypes.func,
  onJournalAppend: PropTypes.func,
  /** A Command Bus prefill. Applied once per distinct object; null means "no prefill". */
  initialConfig: PropTypes.object,
};

export default MOAStrategyDialog;
